import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import HeroLineDraw from "./HeroLineDraw";

/**
 * International-real-estate Hero
 *  - Auto-cycling image slider (4 luxury photos)
 *  - Signature masked line-by-line reveal
 *  - 3D signature tile
 *  - Ken-burns + gentle parallax
 */
const EASE = [0.22, 1, 0.36, 1];

const SLIDES = [
    {
        img: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2400&auto=format&fit=crop",
        caption: "The Verdant Manor · Patia, Bhubaneswar",
        badge: "Ongoing · 2026",
    },
    {
        img: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?q=80&w=2400&auto=format&fit=crop",
        caption: "The Atrium House · Nayapalli",
        badge: "Delivered · 2024",
    },
    {
        img: "https://images.unsplash.com/photo-1493397212122-2b85dda8106b?q=80&w=2400&auto=format&fit=crop",
        caption: "The Marrow Tower · Jaydev Vihar",
        badge: "Upcoming · 2029",
    },
    {
        img: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?q=80&w=2400&auto=format&fit=crop",
        caption: "Kalinga Heights · Kalinga Nagar",
        badge: "Ongoing · 2027",
    },
];

const lineMask = {
    hidden: { y: "112%" },
    visible: (i) => ({
        y: "0%",
        transition: { duration: 1.25, ease: EASE, delay: 0.3 + i * 0.11 },
    }),
};

const SLIDE_INTERVAL = 5000;

export default function Hero() {
    const [idx, setIdx] = useState(0);
    const [paused, setPaused] = useState(false);

    useEffect(() => {
        if (paused) return;
        const id = setInterval(() => setIdx((v) => (v + 1) % SLIDES.length), SLIDE_INTERVAL);
        return () => clearInterval(id);
    }, [paused]);

    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
    const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
    const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.5, 0.78]);

    const slide = SLIDES[idx];

    return (
        <section
            ref={ref}
            data-testid="hero-section"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            className="relative min-h-[100svh] w-full overflow-hidden bg-forest-900 text-ivory"
        >
            {/* PHOTOGRAPHIC BASE — auto-cycling slider */}
            <motion.div
                style={{ y: imgY, scale: imgScale }}
                className="absolute inset-0 -inset-y-12 will-change-transform"
            >
                <AnimatePresence mode="sync">
                    <motion.img
                        key={slide.img}
                        src={slide.img}
                        alt={slide.caption}
                        initial={{ opacity: 0, scale: 1.06 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 1.02 }}
                        transition={{ duration: 1.4, ease: EASE }}
                        className="absolute inset-0 h-full w-full object-cover"
                    />
                </AnimatePresence>
            </motion.div>

            {/* Overlays */}
            <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-forest-950" />
            <div className="absolute inset-0 bg-gradient-to-t from-forest-950 via-forest-950/40 to-forest-950/70" />
            <div className="absolute inset-0 bg-gradient-to-r from-forest-950/85 via-forest-950/30 to-transparent" />

            {/* Vertical grid */}
            <div className="pointer-events-none absolute inset-0 opacity-[0.06]">
                <div className="mx-auto max-w-[1440px] h-full px-6 md:px-10 grid grid-cols-12">
                    {Array.from({ length: 12 }).map((_, i) => (
                        <div key={i} className="border-l border-ivory/30 h-full" />
                    ))}
                </div>
            </div>

            <div className="relative mx-auto max-w-[1440px] px-6 md:px-10 pt-40 md:pt-44 pb-14 md:pb-20 min-h-[100svh] flex flex-col">
                {/* Overline */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
                    className="flex items-center gap-4 mb-10"
                >
                    <span className="h-px w-10 bg-lime" />
                    <span className="font-mono-mini text-[10px] tracking-[0.42em] uppercase text-lime">
                        Premium developer · Bhubaneswar · Odisha
                    </span>
                </motion.div>

                {/* Kinetic display */}
                <h1 className="font-serif-display leading-[0.94] tracking-tight text-[3.2rem] sm:text-[4.6rem] md:text-[6rem] lg:text-[7.4rem] max-w-[17ch]">
                    <span className="mask-line" data-testid="hero-line-1">
                        <motion.span custom={0} initial="hidden" animate="visible" variants={lineMask}>
                            Iconic
                        </motion.span>
                    </span>
                    <span className="mask-line" data-testid="hero-line-2">
                        <motion.span custom={1} initial="hidden" animate="visible" variants={lineMask}>
                            addresses,
                        </motion.span>
                    </span>
                    <span className="mask-line" data-testid="hero-line-3">
                        <motion.span custom={2} initial="hidden" animate="visible" variants={lineMask}>
                            <em className="not-italic italic text-lime">quietly</em> engineered
                        </motion.span>
                    </span>
                    <span className="mask-line" data-testid="hero-line-4">
                        <motion.span custom={3} initial="hidden" animate="visible" variants={lineMask}>
                            to endure.
                        </motion.span>
                    </span>
                </h1>

                {/* Description */}
                <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10">
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.05, ease: EASE, delay: 1.05 }}
                        className="md:col-span-5 text-ivory/85 text-[1rem] md:text-[1.08rem] leading-relaxed max-w-md font-light"
                    >
                        KLiving is a premium construction and development company crafting
                        luxury residential, commercial and modern infrastructure — from
                        concept to completion, at world-class standards.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.05, ease: EASE, delay: 1.2 }}
                        className="md:col-span-4 md:col-start-8 flex md:justify-end items-end"
                    >
                        <a
                            href="#projects"
                            data-testid="hero-primary-cta"
                            className="group inline-flex items-center gap-4"
                        >
                            <span className="h-14 w-14 md:h-16 md:w-16 flex items-center justify-center bg-lime text-forest-900 group-hover:bg-lime-hi transition-colors">
                                <ArrowDown size={20} strokeWidth={1.5} />
                            </span>
                            <span className="font-mono-mini text-[11px] tracking-[0.32em] uppercase text-ivory group-hover:text-lime transition-colors">
                                Explore the collection
                            </span>
                        </a>
                    </motion.div>
                </div>

                {/* Slide caption + counter */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 1.4 }}
                    className="mt-10 md:mt-14 flex items-center gap-6 md:gap-8"
                >
                    <div className="flex items-center gap-2 font-mono-mini text-[10px] tracking-[0.32em] uppercase text-ivory/75">
                        <MapPin size={12} className="text-lime" />
                        <AnimatePresence mode="wait">
                            <motion.span
                                key={slide.caption}
                                initial={{ y: 8, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                exit={{ y: -8, opacity: 0 }}
                                transition={{ duration: 0.5, ease: EASE }}
                            >
                                {slide.caption}
                            </motion.span>
                        </AnimatePresence>
                    </div>
                    <span className="h-px w-16 bg-white/20" />
                    <AnimatePresence mode="wait">
                        <motion.span
                            key={slide.badge}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.4 }}
                            className="font-mono-mini text-[10px] tracking-[0.32em] uppercase text-lime"
                        >
                            {slide.badge}
                        </motion.span>
                    </AnimatePresence>
                </motion.div>

                {/* Slider dots */}
                <div className="mt-6 flex items-center gap-3">
                    {SLIDES.map((_, i) => (
                        <button
                            key={i}
                            data-testid={`hero-slide-dot-${i}`}
                            onClick={() => setIdx(i)}
                            className="group flex items-center"
                            aria-label={`Go to slide ${i + 1}`}
                        >
                            <span className={`relative h-[2px] transition-all duration-500 ${i === idx ? "w-14 bg-lime" : "w-6 bg-ivory/25 group-hover:bg-ivory/50"}`}>
                                {i === idx && (
                                    <motion.span
                                        key={`fill-${i}-${paused}`}
                                        className="absolute inset-y-0 left-0 bg-ivory/40"
                                        initial={{ width: "0%" }}
                                        animate={{ width: paused ? "0%" : "100%" }}
                                        transition={{ duration: SLIDE_INTERVAL / 1000, ease: "linear" }}
                                    />
                                )}
                            </span>
                        </button>
                    ))}
                    <span className="font-mono-mini text-[10px] tracking-[0.32em] uppercase text-ivory/50 ml-2">
                        {String(idx + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}
                    </span>
                </div>

                {/* Signature line-draw tile — top right */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.2, ease: EASE, delay: 1.35 }}
                    className="hidden xl:block absolute right-10 top-32 w-[280px] aspect-[4/3] border border-lime/25 bg-forest-950/50 backdrop-blur-md overflow-hidden shadow-2xl shadow-forest-950/50"
                >
                    <HeroLineDraw className="h-full w-full p-3" />
                    <div className="absolute inset-x-0 bottom-0 p-3 border-t border-lime/20 bg-forest-950/85 backdrop-blur">
                        <div className="font-mono-mini text-[9px] tracking-[0.32em] uppercase text-lime/85 mb-0.5">
                            Live · From line to landmark
                        </div>
                        <div className="font-serif-display text-base leading-tight text-ivory">
                            A building, drawn in real time
                        </div>
                    </div>
                </motion.div>

                {/* Stats */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1.5 }}
                    className="mt-auto pt-14 md:pt-20 grid grid-cols-2 md:grid-cols-4 gap-8 border-t border-white/15"
                >
                    {[
                        { k: "18", l: "Years, patient" },
                        { k: "37", l: "Projects, ever" },
                        { k: "06", l: "Cities in Odisha" },
                        { k: "12", l: "Design awards" },
                    ].map((s, i) => (
                        <div key={s.l} className="pt-6" data-testid={`hero-stat-${i}`}>
                            <div className="font-serif-display text-4xl md:text-5xl text-ivory leading-none">
                                {s.k}
                            </div>
                            <div className="font-mono-mini text-[10px] tracking-[0.28em] uppercase text-ivory/60 mt-3">
                                {s.l}
                            </div>
                        </div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
}
